import React, { useState } from "react";
import { ChevronDown, ChevronRight, FileText, ListChecks } from "./icons";
import type { Cycle } from "./cycles";
import { formatUnitDisplayTitle } from "./formatUnitDisplayTitle";

export { formatUnitDisplayTitle } from "./formatUnitDisplayTitle";

interface Props {
  cycle: Cycle;
  /** When true, show compact view (collapsed units by default) */
  defaultCollapsed?: boolean;
  /** Optional class for the container */
  className?: string;
  /** When true, remove max-height constraint for full-page layout */
  fullPage?: boolean;
  isDarkMode?: boolean;
}

/** Strip leading "a.", "b.", "1.", "2." etc. from criteria text to avoid duplicate bullets. */
function stripLeadingBullet(text: string): string {
  const match = text.match(/^([a-z]|\d+)\.\s+/i);
  return match ? text.slice(match[0].length) : text;
}

/** Displays the full structure of a competency cycle: units, elements, and criteria (demonstrate + explain). */
export function CycleStructureView({
  cycle,
  defaultCollapsed = true,
  className = "",
  fullPage = false,
  isDarkMode = false,
}: Props) {
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(defaultCollapsed ? new Set() : new Set(cycle.units.map((u) => u.id)));
  const [expandedElements, setExpandedElements] = useState<Set<string>>(new Set());

  const toggleUnit = (id: string) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleElement = (unitId: string, elementId: string) => {
    const key = `${unitId}:${elementId}`;
    setExpandedElements((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const totalCriteria = cycle.units.reduce(
    (acc, u) =>
      acc +
      u.elements.reduce((a, e) => a + e.demonstrate.length + e.explain.length, 0),
    0
  );

  return (
    <div
      className={`overflow-hidden rounded-xl border ${
        isDarkMode ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-slate-50/50"
      } ${className}`}
    >
      <div
        className={`flex items-center justify-between border-b px-4 py-3 ${
          isDarkMode ? "border-slate-700 bg-slate-800/80" : "border-slate-200 bg-slate-100"
        }`}
      >
        <h4 className={`font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}>{cycle.name}</h4>
        <span className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
          {cycle.units.length} units · {cycle.units.reduce((a, u) => a + u.elements.length, 0)} elements · {totalCriteria} criteria
        </span>
      </div>
      <div className={`divide-y overflow-y-auto ${isDarkMode ? "divide-slate-700" : "divide-slate-200"} ${fullPage ? "max-h-none" : "max-h-[400px]"}`}>
        {cycle.units.map((unit) => {
          const isUnitExpanded = expandedUnits.has(unit.id);
          return (
            <div key={unit.id} className={isDarkMode ? "bg-slate-900" : "bg-white"}>
              <button
                type="button"
                onClick={() => toggleUnit(unit.id)}
                className={`flex w-full items-center gap-2 px-4 py-3 text-left transition-colors ${
                  isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-50"
                }`}
              >
                {isUnitExpanded ? (
                  <ChevronDown className={`h-4 w-4 shrink-0 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`} />
                ) : (
                  <ChevronRight className={`h-4 w-4 shrink-0 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`} />
                )}
                <span className={`font-medium ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}>{formatUnitDisplayTitle(unit)}</span>
                <span className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  {unit.elements.length} element{unit.elements.length !== 1 ? "s" : ""}
                </span>
              </button>
              {isUnitExpanded && (
                <div className={`space-y-3 border-t pb-4 pl-6 pr-4 ${isDarkMode ? "border-slate-800" : "border-slate-100"}`}>
                  {unit.elements.map((element) => {
                    const key = `${unit.id}:${element.id}`;
                    const isElementExpanded = expandedElements.has(key);
                    const criteriaCount = element.demonstrate.length + element.explain.length;
                    return (
                      <div
                        key={element.id}
                        className={`overflow-hidden rounded-lg border ${
                          isDarkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50/50"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleElement(unit.id, element.id)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${
                            isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100/80"
                          }`}
                        >
                          {isElementExpanded ? (
                            <ChevronDown className={`h-3.5 w-3.5 shrink-0 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`} />
                          ) : (
                            <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`} />
                          )}
                          <span className={`text-sm font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{element.title}</span>
                          <span className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{criteriaCount} criteria</span>
                        </button>
                        {isElementExpanded && (
                          <div className="space-y-3 px-3 pb-3 pt-1">
                            {element.demonstrate.length > 0 && (
                              <div>
                                <div
                                  className={`mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
                                    isDarkMode ? "text-indigo-300" : "text-indigo-700"
                                  }`}
                                >
                                  <ListChecks className="h-3.5 w-3.5" />
                                  Demonstrate ({element.demonstrate.length})
                                </div>
                                <div className={`space-y-1 pl-4 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                                  {element.demonstrate.map((c, i) => (
                                    <div key={i}>
                                      {String.fromCharCode(97 + i)}. {stripLeadingBullet(c)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {element.explain.length > 0 && (
                              <div>
                                <div
                                  className={`mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
                                    isDarkMode ? "text-slate-300" : "text-slate-600"
                                  }`}
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                  Explain ({element.explain.length})
                                </div>
                                <div className={`space-y-1 pl-4 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                                  {element.explain.map((c, i) => (
                                    <div key={i}>
                                      {i + 1}. {stripLeadingBullet(c)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
